/**
 * The FREE trial checkpoint (client, 21/09/2026).
 *
 * The course payload carries `trial_checkpoint` when the backend has it on:
 *   { day, date, confirmed_at, confirmed_menu_day, trials_left }
 * `day` is the first menu day unlocked by the module flagged in Corsi, `date`
 * its date in the member's programme week. From that day on, a menu opens only
 * once the member taps "Continua la prova" (TrialCheckpointModal). Without the
 * field (switched off, older backend), nothing changes.
 *
 * The backend enforces the same rule on the menu endpoint; this only decides
 * when to ask.
 */
import dayjs from "dayjs";

export const TRIAL_CHECKPOINT_ERROR_CODE = "free_trial_checkpoint";

/** Handles both course payload shapes: { course: {...} } and the course itself. */
export const getTrialCheckpoint = (courseData) =>
  courseData?.course?.trial_checkpoint ?? courseData?.trial_checkpoint ?? null;

/**
 * Whether the menu of `calendarDate` waits for "Continua la prova": the
 * checkpoint day or a later day of the same programme week, while the trial is
 * unconfirmed.
 */
export const needsTrialConfirmation = (checkpoint, calendarDate) => {
  if (!checkpoint || checkpoint.confirmed_at || !calendarDate) return false;

  const checkpointDate = dayjs(checkpoint.date).startOf("day");
  const day = Number(checkpoint.day);
  if (!checkpointDate.isValid() || !Number.isFinite(day)) return false;

  const weekEnd = checkpointDate.add(7 - day, "day");
  const target = dayjs(calendarDate).startOf("day");

  return !target.isBefore(checkpointDate) && !target.isAfter(weekEnd);
};

/**
 * The trials the member has left, as the popup names them: the real number,
 * "+1" from the CRM included (client). The current trial is among them until
 * confirmed.
 */
export const trialsLeftPhrase = (trialsLeft) => {
  if (trialsLeft === null || trialsLeft === undefined) {
    return "uno dei tuoi accessi gratuiti";
  }
  if (Number(trialsLeft) <= 1) return "l'ultimo accesso gratuito disponibile";

  return `uno dei ${trialsLeft} accessi gratuiti disponibili`;
};

export const TRIAL_CHECKPOINT_TITLE = "Vuoi continuare la prova gratuita?";

export const trialCheckpointMessage = (trialsLeft) =>
  "Hai già avuto modo di scoprire i primi giorni del percorso Dieta Social. " +
  "Continuando, questa prova verrà considerata utilizzata e non potrà più essere " +
  `annullata senza consumare ${trialsLeftPhrase(trialsLeft)}. ` +
  "Potrai continuare a seguire il percorso Free fino alla sua conclusione.";
